// Голосовое с сайта: запись микрофона в WAV 16 кГц моно. MediaRecorder в разных браузерах пишет разное
// (Chrome — webm, Safari — mp4), а WAV понимает Gemini, который расшифровывает голос (как в Telegram).
export const VOICE_RATE = 16_000;
/** Дольше — останавливаем сами: расшифровке хватает, запрос не разрастается. */
export const VOICE_MAX_SECONDS = 180;

export interface Recording {
  /** Остановить и получить запись. */
  stop: () => Blob;
  /** Бросить без расшифровки. */
  cancel: () => void;
}

export async function startRecording(): Promise<Recording> {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error('Браузер не даёт доступ к микрофону');
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
  });
  const Ctx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctx();
  const source = ctx.createMediaStreamSource(stream);
  // ScriptProcessor устарел, но работает везде, включая Safari на iPhone; выход у него — тишина
  const node = ctx.createScriptProcessor(4096, 1, 1);
  const chunks: Float32Array[] = [];
  node.onaudioprocess = (e) => chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
  source.connect(node);
  node.connect(ctx.destination);
  const close = () => {
    node.disconnect();
    source.disconnect();
    for (const t of stream.getTracks()) t.stop();
    void ctx.close();
  };
  return {
    stop: () => {
      close();
      return toWav(chunks, ctx.sampleRate);
    },
    cancel: close,
  };
}

/** Звук частоты inRate → WAV 16 кГц, 16 бит, моно (понижение частоты усреднением окна). */
export function toWav(chunks: Float32Array[], inRate: number): Blob {
  const total = chunks.reduce((a, c) => a + c.length, 0);
  const all = new Float32Array(total);
  let off = 0;
  for (const c of chunks) {
    all.set(c, off);
    off += c.length;
  }
  const ratio = inRate / VOICE_RATE;
  const n = Math.floor(total / ratio);
  const pcm = new Int16Array(n);
  for (let i = 0; i < n; i++) {
    const from = Math.floor(i * ratio);
    const to = Math.max(from + 1, Math.min(total, Math.floor((i + 1) * ratio)));
    let sum = 0;
    for (let j = from; j < to; j++) sum += all[j] ?? 0;
    pcm[i] = Math.round(Math.max(-1, Math.min(1, sum / (to - from))) * 0x7fff);
  }
  const buf = new ArrayBuffer(44 + pcm.length * 2);
  const dv = new DataView(buf);
  const str = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i));
  };
  str(0, 'RIFF');
  dv.setUint32(4, 36 + pcm.length * 2, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  dv.setUint32(16, 16, true);
  dv.setUint16(20, 1, true); // PCM
  dv.setUint16(22, 1, true); // моно
  dv.setUint32(24, VOICE_RATE, true);
  dv.setUint32(28, VOICE_RATE * 2, true);
  dv.setUint16(32, 2, true);
  dv.setUint16(34, 16, true);
  str(36, 'data');
  dv.setUint32(40, pcm.length * 2, true);
  new Int16Array(buf, 44).set(pcm);
  return new Blob([buf], { type: 'audio/wav' });
}

/** Понятная причина, почему микрофон не включился. */
export function micError(err: unknown): string {
  const name = (err as { name?: string } | null)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError')
    return 'Разрешите сайту доступ к микрофону — значок слева от адреса страницы';
  if (name === 'NotFoundError') return 'Микрофон не найден';
  return (err as Error)?.message || 'Микрофон не включился';
}
